import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { MessageService } from 'primeng/api';
import { MenuPermissionConfig, XpressRiderStatus } from 'app/models';
import { MenuHomeEnum } from 'app/components/home';
import { Utils } from 'app/utils';
import {
	OperatorDriversListResponse,
	OperatorDriversQuery,
	OperatorSingleInviteRequest
} from 'app/models/operator/operator-drivers-query.model';
import { RiderService } from '../rider-service';

export interface BackOfficeTableContext {
	getDriverType(): string;
	getMenuValue(): string;
}

@Injectable({
	providedIn: 'root'
})
export class OperatorDriversService {

	constructor(private riderService: RiderService) { }

	isOperatorDriversMenu(menuPermissionActive?: string): boolean {
		if (!menuPermissionActive) {
			return false;
		}

		return menuPermissionActive.includes('active-drivers')
			|| menuPermissionActive === MenuPermissionConfig.BACK_OFFICE_OPERATOR_DRIVERS
			|| menuPermissionActive.includes('back-office-drivers-driver-passenger')
			|| menuPermissionActive.includes('back-office-drivers-taxi')
			|| menuPermissionActive.includes('back-office-drivers-connex');
	}

	getTableContextName(
		menuPermissionActive?: string,
		backOfficeRoot?: BackOfficeTableContext | null
	): string {
		if (backOfficeRoot) {
			return `${backOfficeRoot.getDriverType()}-${backOfficeRoot.getMenuValue()}`;
		}

		if (this.isOperatorDriversMenu(menuPermissionActive)) {
			return 'Operator-Active Drivers';
		}

		return 'Drivers';
	}

	/**
	 * Display names use a trailing period on the last initial (e.g. "Juan D.").
	 * Backend search splits on spaces and requires each token to match FirstName/LastName/etc
	 * via Contains, so a pasted "D." token fails. Strip trailing periods from tokens.
	 */
	normalizeDriverSearchQuery(value?: string | null): string {
		if (value == null) {
			return '';
		}

		const normalized = String(value)
			.split(/\s+/)
			.filter(Boolean)
			.map(token => token.replace(/\.+$/, ''))
			.filter(Boolean)
			.join(' ');

		return normalized;
	}

	buildGetAdminRidersRequest(query: OperatorDriversQuery): Record<string, unknown> {
		const dateStart = new Date();
		const dateEnd = new Date();

		return {
			ClientLocationType: query.clientLocationType,
			OperatorId: query.operatorId,
			InvitedDriversOnly: !!query.invitedDriversOnly,
			SearchName: this.normalizeDriverSearchQuery(query.filter.searchName),
			StartDateJoin: query.invitedDriversOnly
				? null
				: query.filter.startDateJoin
					? moment(query.filter.startDateJoin).format('YYYY-MM-DD')
					: moment(dateStart).format('YYYY-MM-DD'),
			EndDateJoin: query.invitedDriversOnly
				? null
				: query.filter.endDateJoin
					? moment(query.filter.endDateJoin).format('YYYY-MM-DD')
					: moment(dateEnd.setDate(dateEnd.getDate() + 1)).format('YYYY-MM-DD'),
			Id: query.headerSearch.driverId === null ? query.sessionDriverId : query.headerSearch.driverId,
			UserName: this.normalizeDriverSearchQuery(query.headerSearch.driverName),
			Phone: query.headerSearch.phone,
			Vehicle: query.headerSearch.vehicle,
			PlateNumber: query.headerSearch.plateNumber,
			Email: query.headerSearch.email,
			CountTrips: query.headerSearch.countTrips,
			CompleteRate: query.headerSearch.completeRate,
			AcceptanceRate: query.headerSearch.acceptanceRate,
			CancellationRate: query.headerSearch.cancellationRate,
			LastActiveType: query.headerSearch.lastActiveTypeObj?.value ?? null,
			LastActiveAt: query.headerSearch.lastActiveTypeObj?.dateTime
				? moment(query.headerSearch.lastActiveTypeObj.dateTime).format('YYYY-MM-DDTHH:mm:ss')
				: null,
			LastLogin: query.headerSearch.lastlogin,
			Status: query.headerSearch.status,
			Rating: query.headerSearch.rating,
			ServiceTypes: query.driverServiceTypes,
			VehicleTypes: query.driverVehicleTypes,
			RiderStatuses: query.useActiveDriverStatuses
				? [XpressRiderStatus.RegistrationCompleted, XpressRiderStatus.Used]
				: null,
			SignupDate: query.headerSearch.signupDate
				? moment(query.headerSearch.signupDate).format('YYYY-MM-DDTHH:mm:ss')
				: null,
			Region: query.headerSearch.region,
			Service: query.headerSearch.service,
			DriverCampaign: query.headerSearch.driverCampaign,
			ProbationExpired: query.headerSearch.probationExpired,
			ProbationExpiring: query.headerSearch.probationExpiring,
			RiderSourceType: query.headerSearch.riderSourceType,
			Filter: {
				ServiceType: query.filter.serviceType ?? null,
				VehicleType: query.filter.vehicleType ?? null,
				SignupDate: query.filter.signupDate
					? moment(query.filter.signupDate).format('YYYY-MM-DDTHH:mm:ss')
					: null
			},
			Skip: query.skip,
			Take: query.take,
			SortField: query.sortField,
			SortOrder: query.sortOrder,
			TimeZoneOffset: Utils.getTimezoneOffset(),
			RiderType: query.riderType
		};
	}

	formatDriverDisplayName(
		firstName?: string | null,
		lastName?: string | null,
		fallback?: string | null
	): string {
		const first = (firstName ?? '').trim();
		const last = (lastName ?? '').trim();

		if (first && last) {
			return `${first} ${last.charAt(0).toUpperCase()}.`;
		}

		if (first) {
			return first;
		}

		if (last) {
			return `${last.charAt(0).toUpperCase()}.`;
		}

		const fallbackName = (fallback ?? '').trim();
		if (!fallbackName) {
			return '';
		}

		const parts = fallbackName.split(/\s+/).filter(Boolean);
		if (parts.length >= 2) {
			const lastPart = parts[parts.length - 1];
			return `${parts.slice(0, -1).join(' ')} ${lastPart.charAt(0).toUpperCase()}.`;
		}

		return fallbackName;
	}

	formatWholeNumberPercent(value: unknown): string {
		if (value === '-' || value === null || value === undefined || value === '') {
			return '-';
		}

		const numeric = Number(value);
		if (Number.isNaN(numeric)) {
			return String(value);
		}

		return `${Math.round(numeric)}%`;
	}

	formatOperatorKpiAmount(amount: string | null | undefined): string {
		if (!amount) {
			return amount ?? '';
		}

		const match = String(amount).trim().match(/^([\d.,]+)\s*(%?)$/);
		if (!match) {
			return amount;
		}

		const numeric = Number(match[1].replace(/,/g, ''));
		if (Number.isNaN(numeric)) {
			return amount;
		}

		const rounded = Math.round(numeric);
		return match[2] ? `${rounded}%` : rounded.toLocaleString('en-US');
	}

	normalizeInvitedRiderRows(items: unknown[]): unknown[] {
		return items.map((item: any) => ({
			...item,
			UserName: `${item.FirstName ?? ''} ${item.LastName ?? ''}`.trim()
		}));
	}

	async fetchDrivers(
		query: OperatorDriversQuery,
		messageService: MessageService
	): Promise<OperatorDriversListResponse> {
		const dto = this.buildGetAdminRidersRequest(query);
		const data = await this.riderService.getAdminRiders(dto, messageService);

		if (!data) {
			return {
				items: [],
				totalRecords: 0,
				cards: [],
				success: false
			};
		}

		if (!data.Success) {
			return {
				items: [],
				totalRecords: 0,
				cards: data.Cards ?? [],
				success: false,
				errorMessage: data.ErrorMessage
			};
		}

		return {
			items: this.normalizeInvitedRiderRows(data.Items ?? []),
			totalRecords: data.TotalRecords ?? 0,
			cards: data.Cards ?? [],
			success: true
		};
	}

	validateInvitePhone(phone: string, messageService: MessageService): Promise<any> {
		return this.riderService.validateInvitePhone({ Phone: phone }, messageService);
	}

	sendSingleInvite(
		request: OperatorSingleInviteRequest,
		messageService: MessageService
	): Promise<unknown> {
		return this.riderService.sendSingleInvite({
			FirstName: request.firstName,
			LastName: request.lastName,
			Phone: request.phone,
			Email: request.email,
			RiderLocationType: request.riderLocationType
		}, messageService);
	}

	resendInvitedRider(
		id: number,
		riderLocationType: unknown,
		messageService: MessageService
	): Promise<unknown> {
		return this.riderService.resendInvitedRider({
			Id: id,
			RiderLocationType: riderLocationType
		}, messageService);
	}
}
